import { ModalBody, useToast, VStack } from '@chakra-ui/react';
import { InputFormControl } from 'app/shared-components';
import { useFormik } from 'formik';
import { useProjectMutations } from 'app/features/project/hooks/useProject';
import { EditProjectFormValues } from 'app/features/project/shared/types/form';
import { useProjectIntegration } from '../../hooks';
import { useRepositoryHookSubscriptionMutation } from 'app/services/repositories';

import { parseADORepository } from './helpers';
import { handleSubmitError, createSubmitHandlers } from './handlers';
import { FormActions } from './FormActions';
import { projects } from '@xmcloud/core/messages/en';
import { handlePush } from '@xmcloud/utils/helpers';
import { goTo } from '@xmcloud/core/routes/paths';
import { t } from '@transifex/native';
import { useFormState } from './helpers/formState';
import { editProjectNameOnlySchema } from '../../shared/validation/editProjectSchema';
import { hasSourceControlChanges } from './helpers/hasSourceControlChanges';
import { ESourceControl } from '@xmcloud/types';
import config from 'app/config/config';
import { useQueryClient } from 'react-query';

const { projectDetailsEdit } = projects;
const { queryKey } = config.projects.project;

/**
 * Modal form for editing the project name. Source control fields remain in form state for
 * submit/reinitialize but are not shown or edited here.
 */
export const EditProjectForm = ({
	projectId,
	onClose,
}: {
	projectId: string;
	onClose: () => void;
}) => {
	const queryClient = useQueryClient();
	const { project, repository, integration } =
		useProjectIntegration(projectId);
	const mutations = useProjectMutations(projectId);
	const subscribeHookMutation = useRepositoryHookSubscriptionMutation();

	const { adoOrganization, adoProjectName, adoRepository } =
		parseADORepository(project?.repository!!);
	const submitHandlers = createSubmitHandlers({
		projectId,
		mutations,
		subscribeHook: subscribeHookMutation,
	});

	const toast = useToast();
	const noDefaultRepository =
		!project?.repository || !project?.sourceControlIntegrationId;

	const formik = useFormik<EditProjectFormValues>({
		initialValues: {
			name: project?.name ?? '',
			provider: noDefaultRepository
				? ''
				: repository?.provider === 2
					? ESourceControl.ADO
					: ESourceControl.GitHub,
			repository: project?.repository ?? '',
			repositoryId: project?.repositoryId ?? '',
			sourceControlIntegrationId:
				project?.sourceControlIntegrationId ?? '',
			adoOrganization,
			adoProjectName,
			adoRepository,
		},
		validationSchema: editProjectNameOnlySchema,
		enableReinitialize: true,
		validateOnBlur: false,
		onSubmit: async (values) => {
			const nameChanged = values.name !== project?.name;
			const repoChanged = hasSourceControlChanges({
				formik,
				project: project!,
			});

			try {
				if (nameChanged && repoChanged) {
					await submitHandlers.handleBothChanges(values);
				} else if (nameChanged) {
					await submitHandlers.handleNameChange(values);
				} else if (repoChanged) {
					await submitHandlers.handleRepoChange(values);
				}

				toast({
					status: 'success',
					description: t(projectDetailsEdit),
				});
				queryClient.invalidateQueries(queryKey(projectId));
				handlePush(goTo.projectDetails(projectId));
			} catch (error: unknown) {
				handleSubmitError(error, {
					toast,
					setFieldValue: formik.setFieldValue,
				});
			}
		},
	});

	const { isRepositoryChanged, hasAccount, isSubmitDisabled } = useFormState({
		formik,
		project,
		repository,
		integration,
		mutations,
		nameOnlyEditing: true,
	});

	return (
		<form
			data-testid="edit-project-form"
			onSubmit={formik.handleSubmit}
			noValidate={true}
		>
			<ModalBody>
				<VStack spacing={4} align="stretch">
					<InputFormControl
						label="Project Name"
						name="name"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						isInvalid={Boolean(
							formik.touched.name && formik.errors.name,
						)}
						error={
							formik.touched.name ? formik.errors.name : undefined
						}
						isRequired
					/>
				</VStack>
			</ModalBody>
			<FormActions
				isSubmitting={formik.isSubmitting}
				isDisabled={isSubmitDisabled}
				isRepositoryChanged={integration.isValid && isRepositoryChanged}
				hasAccount={hasAccount}
				hasError={Boolean(formik.errors.name)}
				onClose={onClose}
			/>
		</form>
	);
};
